import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  message,
  Modal,
  Pagination,
  Space,
  Table,
  Tag,
  Tabs,
  Typography,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
  CloseCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  deleteFaqSourceFilesApi,
  getFaqSourceDownloadApi,
  listFaqSourcesApi,
  uploadFaqSourceFileApi,
} from "../../services/faq.service";
import PdfViewer from "../../components/pdf-viewer";

const { Dragger } = Upload;
const { Text } = Typography;

const FaqFileManagerDialog = ({ open, onClose, onChanged, onViewFaqs }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("upload");
  const [uploadFileList, setUploadFileList] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [sources, setSources] = useState([]);
  const [sourcesPage, setSourcesPage] = useState(1);
  const sourcesLimit = 1000;
  const [sourcesTotal, setSourcesTotal] = useState(0);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [selectedSourceIds, setSelectedSourceIds] = useState([]);
  const [deleteFaqs, setDeleteFaqs] = useState(false);
  const deleteFaqsRef = useRef(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [localPdfPreviewUrl, setLocalPdfPreviewUrl] = useState("");
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");
  const [previewPdfTitle, setPreviewPdfTitle] = useState("");
  const tabBodyHeight = "calc(88vh - 165px)";
  const sharedContentHeight = "calc(88vh - 290px)";

  const getErrorMessage = (error) =>
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong";

  const isPdfFile = (file) => {
    if (!file) return false;
    const type = String(file.type || "").toLowerCase();
    const extension = getFileExtension(file.name).toUpperCase();
    return type === "application/pdf" || extension === "PDF";
  };

  const getFileExtension = (filename = "") => {
    const normalized = String(filename || "").trim();
    const lastDot = normalized.lastIndexOf(".");
    if (lastDot === -1) return "";
    return normalized.slice(lastDot + 1).toUpperCase();
  };

  const fetchSources = (page = sourcesPage, limit = sourcesLimit) => {
    dispatch(
      listFaqSourcesApi({
        page,
        limit,
        finalCallback: () => setSourcesLoading(false),
        successCallback: (data) => {
          setSources(data?.items || []);
          setSourcesTotal(data?.total || 0);
        },
        failureCallback: (error) => {
          message.error(`Failed to load files: ${getErrorMessage(error)}`);
          setSources([]);
          setSourcesTotal(0);
        },
      })
    );
  };

  useEffect(() => {
    if (!open) return;
    setSourcesLoading(true);
    fetchSources(1, sourcesLimit);
    setSourcesPage(1);
    setSelectedSourceIds([]);
    setDeleteFaqs(false);
    deleteFaqsRef.current = false;
  }, [open]);

  useEffect(() => {
    const selectedFile = uploadFileList[0]?.originFileObj;
    if (!selectedFile) {
      setLocalPdfPreviewUrl("");
      return;
    }

    if (!isPdfFile(selectedFile)) {
      setLocalPdfPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setLocalPdfPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [uploadFileList]);

  const handleUpload = () => {
    const file = uploadFileList[0]?.originFileObj;
    if (!file) {
      message.warning("Select a PDF file first.");
      return;
    }

    if (!isPdfFile(file)) {
      message.error("Only PDF files are allowed. Please select a valid PDF file.");
      return;
    }

    setIsUploading(true);
    dispatch(
      uploadFaqSourceFileApi({
        file,
        finalCallback: () => setIsUploading(false),
        successCallback: (response) => {
          const createdCount = response?.batch_results?.results?.length || 0;
          message.success(
            createdCount
              ? `File uploaded successfully. ${createdCount} FAQs processed.`
              : "File uploaded successfully."
          );
          setUploadFileList([]);
          setActiveTab("files");
          setSourcesLoading(true);
          setSourcesPage(1);
          fetchSources(1, sourcesLimit);
          if (onChanged) onChanged();
        },
        failureCallback: (error) => {
          message.error(`Upload failed: ${getErrorMessage(error)}`);
        },
      })
    );
  };

  const handleViewFile = (row) => {
    const sourceId = row?.id;
    if (!sourceId) {
      message.error("Invalid source file selected.");
      return;
    }

    setDownloadingId(sourceId);
    dispatch(
      getFaqSourceDownloadApi({
        sourceId,
        finalCallback: () => setDownloadingId(null),
        successCallback: (response) => {
          const downloadUrl =
            response?.download_url || response?.presigned_url;
          if (!downloadUrl) {
            message.error("No download URL received.");
            return;
          }

          const filename = row?.filename || row?.name || `Document ${sourceId}`;
          const extension = getFileExtension(filename);
          const extensionFromUrl = getFileExtension(downloadUrl?.split("?")?.[0]);
          const isPdf = extension === "PDF";
          const isUrlPdf = extensionFromUrl === "PDF";

          if (!isPdf && !isUrlPdf) {
            window.open(downloadUrl, "_blank");
            return;
          }

          setPreviewPdfTitle(filename);
          setPreviewPdfUrl(downloadUrl);
          setIsPdfViewerOpen(true);
        },
        failureCallback: (error) => {
          message.error(`Failed to get download link: ${getErrorMessage(error)}`);
        },
      })
    );
  };

  const handleViewFaqs = (row) => {
    const sourceId = row?.id;
    if (!sourceId) {
      message.error("Invalid source file selected.");
      return;
    }
    const sourceName = row?.filename || row?.name || `Source ${sourceId}`;

    if (onClose) onClose();
    if (onViewFaqs) onViewFaqs({ sourceId, sourceName });
  };

  const executeDelete = (sourceIds, includeRelatedFaqs = deleteFaqs) => {
    if (!sourceIds.length) {
      message.warning("Select at least one file to delete.");
      return;
    }

    setDeleting(true);
    dispatch(
      deleteFaqSourceFilesApi({
        sourceIds,
        deleteFaqs: includeRelatedFaqs,
        finalCallback: () => setDeleting(false),
        successCallback: () => {
          message.success("Selected file(s) deleted successfully.");
          setSelectedSourceIds((prev) =>
            prev.filter((id) => !sourceIds.includes(id))
          );

          const nextPage =
            sources.length <= sourceIds.length && sourcesPage > 1
              ? sourcesPage - 1
              : sourcesPage;

          setSourcesLoading(true);
          setSourcesPage(nextPage);
          fetchSources(nextPage, sourcesLimit);
          if (onChanged) onChanged();
        },
        failureCallback: (error) => {
          message.error(`Delete failed: ${getErrorMessage(error)}`);
        },
      })
    );
  };

  const handleBatchDelete = () => {
    if (!selectedSourceIds.length) {
      message.warning("Select files to delete.");
      return;
    }
    Modal.confirm({
      title: `Delete ${selectedSourceIds.length} file(s)?`,
      content: (
        <div>
          <div style={{ marginBottom: 12 }}>Source files will be deleted.</div>
          <Checkbox
            className="faq-custom-checkbox faq-custom-checkbox--pill"
            defaultChecked={deleteFaqs}
            onChange={(e) => {
              const checked = e.target.checked;
              setDeleteFaqs(checked);
              deleteFaqsRef.current = checked;
            }}
          >
            Also delete related FAQs
          </Checkbox>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      onOk: () => executeDelete(selectedSourceIds, deleteFaqsRef.current),
    });
  };

  const columns = useMemo(
    () => [
      {
        title: "File Name",
        dataIndex: "filename",
        key: "filename",
        render: (value) => <Text>{value || "-"}</Text>,
      },
      {
        title: "Uploaded At",
        dataIndex: "upload_date",
        key: "upload_date",
        width: 220,
        render: (value) => (value ? new Date(value).toLocaleString([], {
          month: 'long',
          year: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : "-"),      },
      {
        title: "Uploaded By",
        dataIndex: "uploaded_by",
        key: "uploaded_by",
        width: 140,
        render: (value) => value || "-",
      },
      {
        title: "FAQs Count",
        dataIndex: "faq_count",
        key: "faq_count",
        width: 220,
        render: (value, row) => (
          <button
            type="button"
            onClick={() => handleViewFaqs(row)}
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
            }}
            aria-label={`View FAQs for ${row?.filename || row?.name || "source file"}`}
            title="View FAQs for this source"
          >
            <Tag color="blue" style={{ cursor: "pointer" }}>
              {value ?? 0}
            </Tag>
          </button>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 170,
        render: (_, row) => (
          <Space>
            <Button
              icon={<EyeOutlined />}
              size="small"
              loading={downloadingId === row.id}
              onClick={() => handleViewFile(row)}
            >
              View File
            </Button>
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() =>
                Modal.confirm({
                  title: "Delete this source file?",
                  content: (
                    <div>
                      <div style={{ marginBottom: 12 }}>Source file will be deleted.</div>
                      <Checkbox
                        className="faq-custom-checkbox faq-custom-checkbox--pill"
                        defaultChecked={deleteFaqs}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setDeleteFaqs(checked);
                          deleteFaqsRef.current = checked;
                        }}
                      >
                        Also delete related FAQs
                      </Checkbox>
                    </div>
                  ),
                  okText: "Delete",
                  okType: "danger",
                  onOk: () => executeDelete([row.id], deleteFaqsRef.current),
                })
              }
            />
          </Space>
        ),
      },
    ],
    [deleteFaqs, downloadingId, onClose, onViewFaqs]
  );

  return (
    <Modal
      title="Manage FAQ Documents"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      centered
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "88vh",
          overflowY: "auto",
        },
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{
          overflow: "hidden",
        }}
        tabBarExtraContent={
          activeTab === "files" && selectedSourceIds.length > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deleting}
              onClick={handleBatchDelete}
            >
              Delete Selected
            </Button>
          )
        }
        items={[
          {
            key: "upload",
            label: "Upload Document",
            children: (
              <div
                style={{
                paddingTop: 8,
                  height: tabBodyHeight,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {!uploadFileList.length ? (
                  <Dragger
                    accept=".pdf,application/pdf"
                    multiple={false}
                    beforeUpload={() => false}
                    fileList={uploadFileList}
                    onChange={({ fileList }) => {
                      const latest = fileList.slice(-1);
                      const selectedFile = latest?.[0]?.originFileObj;
                      if (!selectedFile) {
                        setUploadFileList([]);
                        return;
                      }

                      if (!isPdfFile(selectedFile)) {
                        message.error("Only PDF files are allowed. Please select a valid PDF file.");
                        setUploadFileList([]);
                        return;
                      }

                      setUploadFileList(latest);
                    }}
                    onRemove={() => setUploadFileList([])}
                    maxCount={1}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag a PDF file to upload
                    </p>
                    <p className="ant-upload-hint">
                      The file will be parsed into FAQs and stored as a source.
                    </p>
                  </Dragger>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: "8px 12px",
                        background: "#fafafa",
                      }}
                    >
                      <Text ellipsis style={{ maxWidth: "85%" }}>
                        {uploadFileList[0]?.name}
                      </Text>
                      <Button
                        type="text"
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => setUploadFileList([])}
                      >
                        Remove
                      </Button>
                    </div>
                    <div
                      style={{
                        marginTop: 12,
                        height: sharedContentHeight,
                        minHeight: sharedContentHeight,
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        overflow: "auto",
                        background: "#fafafa",
                      }}
                    >
                      {localPdfPreviewUrl ? (
                        <PdfViewer
                          pdfUrls={[{ url: localPdfPreviewUrl }]}
                          showDocumentNav={false}
                          noBorder
                        />
                      ) : (
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#8c8c8c",
                            padding: 16,
                          }}
                        >
                          Preview is available for PDF files.
                        </div>
                      )}
                    </div>
                  </>
                )}
                <div
                  style={{
                    position: "sticky",
                    bottom: 0,
                    marginTop: 12,
                    paddingTop: 16,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    background: "#fff",
                    zIndex: 2,
                  }}
                >
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleUpload}
                    loading={isUploading}
                  >
                    Upload and Process
                  </Button>
                  <Button onClick={onClose}>Cancel</Button>
                </div>
              </div>
            ),
          },
          {
            key: "files",
            label: `Existing Documents (${sourcesTotal})`,
            children: (
              <div
                style={{
                  paddingTop: 8,
                  height: tabBodyHeight,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    rowKey="id"
                    loading={sourcesLoading}
                    columns={columns}
                    dataSource={sources}
                    pagination={false}
                    size="middle"
                    sticky
                    rowSelection={{
                      selectedRowKeys: selectedSourceIds,
                      onChange: (keys) => setSelectedSourceIds(keys),
                    }}
                    locale={{
                      emptyText: "No source files uploaded yet.",
                    }}
                  />
                </div>
                {sourcesTotal > sourcesLimit ? (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Pagination
                    current={sourcesPage}
                    pageSize={sourcesLimit}
                    total={sourcesTotal}
                    showSizeChanger={false}
                    showTotal={() => `Total ${sourcesTotal} files`}
                    onChange={(nextPage) => {
                      const targetPage = nextPage;
                      setSourcesPage(targetPage);
                      setSelectedSourceIds([]);
                      setSourcesLoading(true);
                      fetchSources(targetPage, sourcesLimit);
                    }}
                  />
                </div>
                ) : null}
              </div>
            ),
          },
            ]}
          />

          <Modal
            open={isPdfViewerOpen}
            title={previewPdfTitle || "PDF Preview"}
            footer={null}
            onCancel={() => {
              setIsPdfViewerOpen(false);
              setPreviewPdfUrl("");
              setPreviewPdfTitle("");
            }}
            width="90vw"
            style={{ top: 20 }}
            bodyStyle={{ height: "80vh", padding: 0 }}
            destroyOnClose
          >
            {previewPdfUrl ? (
              <PdfViewer
                pdfUrls={[
                  { url: previewPdfUrl, title: previewPdfTitle || "PDF Preview" },
                ]}
                zoomable
                rotatable
                fullscreenable
                downloadable
                showDocumentNav={false}
                noBorder
              />
            ) : null}
          </Modal>
        </Modal>
    );
  };

export default FaqFileManagerDialog;
